// src/components/legal-docs.js
//
// The Terms of Service and Privacy Policy shown at /legal and linked from the
// signup form. Rendered by LegalPage.js through a deliberately small markdown
// converter that understands ONLY: # / ## / ### headings, "- " bullets, and
// blank-line-separated paragraphs. Bold, italics and links render literally —
// do not use them here.
//
// LEGAL_VERSION is the consent record. It is stored on users.terms_version
// when a member accepts, so we can prove which text they agreed to. BUMP IT
// whenever either document changes materially: every member whose stored
// version is older than this is asked to accept again on their next visit.
// Keep the format YYYY-MM-DD (max 32 chars — users.terms_version).
export const LEGAL_VERSION = "2026-08-11";

export const termsOfService = `# Terms of Service

Last updated: August 11, 2026 (version 2026-08-11)

## 1. Who we are, and what you are agreeing to

Glonet is operated by Mind Machine Learning ("Glonet", "we", "us"). These Terms cover glonet.ai, mml-glonet.com and everything offered through them: the nexus and its matching, the Water Cooler, our voice agents, membership, hosting and the affiliate program.

By creating an account or using the services, you agree to these Terms and to the Privacy Policy. If you do not agree, do not use the services.

## 2. Your account

- You must be 18 or older to use Glonet.
- You may create an account with an email address and password, or with Google. Signing in with Google for the first time creates a Glonet account and accepts these Terms.
- Give us accurate information and keep it current. Keep your credentials to yourself. You are responsible for what happens under your account.
- One account per person. Do not share, sell or transfer your account.
- We may suspend or close an account that breaks these Terms.

## 3. Where Glonet is today: early access

Glonet is in early access, and we would rather be plain about it than have you guess:

- Joining is free right now, and it is not capped.
- Matching is not yet running on the glonet.ai nexus. The Water Cooler, the voice profile intake and the rest of the site are live.
- Paid membership exists in the product but billing is not switched on. Nobody is charged today. We will say so clearly before that changes, and no charge will ever happen without you going through checkout and agreeing to a price first.
- Features in early access may change, move or be withdrawn.

Nothing in this section is a promise that any part of Glonet will stay free.

## 4. Membership, subscriptions and billing

### 4.1 What a membership is
Access to a nexus may be free or paid, depending on the nexus and on when you join. A paid membership is a recurring subscription that gives you member access to that nexus: its matching, its member areas and the parts of the Water Cooler reserved for members.

### 4.2 Prices
The price of a membership is shown at checkout before you pay. Any figure you see elsewhere on the site, in an announcement or in a conversation with us is an estimate and not a commitment. The price you pay is the one displayed on the checkout page you complete.

### 4.2a Discounts
Early access and referral discounts can lower your price for a stated period, usually your first year. If you qualify for more than one discount, you get the best single one; discounts do not stack. Once a discount is attached to you it does not get worse: it can be replaced by a deeper one, but it is never reduced or withdrawn before its period ends, as long as your subscription runs without interruption. When the period ends, the then-current full price applies.

### 4.3 Recurring billing and auto-renewal
Subscriptions are billed monthly or annually, whichever you choose. They renew automatically at the same interval, at the then-current price for your plan, until you cancel. Payments are processed by Stripe; we do not receive or store your card number.

### 4.4 Cancelling
You can cancel auto-renewal at any time, from your membership page or from the Stripe billing portal. Cancelling stops the next renewal — it does not end your current period. Your access continues until the end of the period you have already paid for, and then stops.

### 4.5 Failed payments
If a payment fails, Stripe retries it on its normal schedule. Your access continues during those retries. If payment ultimately fails, your membership ends the same way a cancellation does.

### 4.6 What happens when a membership ends
Your member access to that nexus stops, including matching and member-only areas. Your profile, objectives and offerings are kept, so that if you subscribe again your history is still there. You can delete them at any time (see the Privacy Policy).

### 4.7 Price changes
If we change the price of a plan, members who are already subscribed keep the price they signed up at for as long as their subscription runs without interruption. New prices apply to new checkouts, and to a subscription that is cancelled and later restarted.

### 4.8 Refunds
Subscriptions are paid in advance and we do not automatically refund a partial period, including when you cancel mid-period or when we close an account for breach of these Terms. If you think you have been charged in error, write to us and we will look at it; we may issue refunds at our discretion. If we discontinue a paid service that you have prepaid for, we will refund the unused portion. None of this limits refund rights you have under law that cannot be waived.

### 4.9 Taxes
Prices exclude taxes unless stated otherwise. You are responsible for any taxes that apply to you other than taxes on our income.

## 5. Voice, AI, and what is recorded

Glonet is voice-first, and AI runs through most of it. Here is what that means in practice.

### 5.1 Voice profile intake
When you build or update your profile by talking to our voice agent, the conversation is captured and transcribed by our voice provider, and the transcript is processed by AI to draft your objective and offering. You review what it wrote. You can always type instead of speaking.

### 5.2 The Water Cooler
Live audio and video in the Water Cooler is carried by our real-time provider and is not recorded by Glonet. We do keep records of activity: when you entered and left, how long you said you would stay, what you did there, and your conversations with the concierge agent.

### 5.3 The concierge line
The concierge can introduce you to people in the room with a short line drawn from your profile. You are shown that line and asked to approve it before it is ever spoken, and you can decline and have nothing said about you.

### 5.4 Agent conversations
Glonet can arrange for your agent and another member's agent to hold a short conversation before you meet. This feature is currently switched off. If it is turned on, we will say so, it will produce a transcript and audio recording of that agent conversation, and both members will be able to read or hear it.

### 5.5 Presence notifications
When you enter the Water Cooler, we may email members you are already connected to, so they can join you. There is a cooldown so this cannot become a nuisance.

### 5.6 AI is not always right
Text, matches, summaries and spoken lines produced by AI can be wrong, incomplete or oddly worded. Check anything that matters before relying on it. Nothing produced by Glonet or its agents is legal, financial, medical or professional advice.

## 6. What other members can see

Members you are matched or connected with can see your name, your objective and offering, the concierge line if you approved one, and any contact details you chose to add to your profile, such as your email or LinkedIn. Do not put anything in your profile that you would not want another member to have.

## 7. How you must behave

- Use Glonet lawfully and professionally.
- No harassment, hate, threats, spam or deception.
- Do not impersonate anyone, or misrepresent who you are or what you do.
- Do not scrape, crawl or bulk-collect member information, and do not use what you learn on Glonet to build a mailing list or sell to members who did not ask.
- Do not attack, overload or probe the service, or try to reach parts of it you have not been given access to.

We can remove content and suspend or close accounts that break these rules.

## 8. Hosts

If we approve you to host a nexus:

- You set the membership price for your nexus, up to a maximum of $5,000 per month.
- We collect all membership payments and pay you your share. You must complete Stripe Connect onboarding before money can reach you; until you do, your share is held for you rather than lost.
- We keep a platform fee out of what your members pay. The standard fee is 30%, and we may agree a different rate with you in writing. The calculator on our hosts page shows the split for any price you enter, including the progressive scale we apply as prices rise.
- If a member is refunded or charges back, your share of that payment is reversed in proportion.
- You are responsible for your own nexus: what you promise your members, the conduct you allow, and your own taxes.

## 9. Affiliates

If we approve you as an affiliate:

- Standard affiliates earn 25% of what each member they refer actually pays, for that member's first 6 paid months. Partner affiliates earn 50% for the first 12 paid months per referred member. Partner slots are limited.
- Your terms are frozen when the referral happens. If we later change the program, anything already referred keeps the terms it was referred under.
- Your link can carry a discount for the people who join through it, chosen by you from options we set (currently 60% or 40% off the first year). The discount attaches to each person when they claim your link. If you later choose a deeper discount, we also apply it to your referrals who have not started paying yet; a person's discount never gets worse after they claim it.
- Commission is calculated on what your referrals actually pay. A deeper discount means a smaller commission per member while the discount runs.
- Commission is paid on money we actually collect. Cancelled, unpaid or refunded months earn nothing, and a refund reverses commission already credited.
- Months are counted per referred member and do not restart. A member who cancels and returns continues their count where it left off.
- Referring yourself, or accounts you control, earns nothing. We may hold payouts for review where a referral pattern needs checking, and we may suspend an affiliate; suspension holds payouts rather than cancelling what was properly earned.
- The program has no lifetime guarantee beyond the windows stated above.

## 10. Your content

You keep ownership of what you write, say and upload. You give us permission to store it, display it to the members it is meant for, and process it to run and improve Glonet, including with the AI providers we use. You also let us use your feedback and how you use the product to improve our matching and our service, without payment.

## 11. Service availability

We do not promise that Glonet will always be available, that matching will produce a match, or that any match will be useful. We may change or discontinue features. We will try to give notice of significant changes.

## 12. Ending this agreement

You can stop using Glonet and delete your account at any time. We can suspend or close your account if you break these Terms, if we are required to, or if we discontinue the service. If we close a paid account for something other than a breach by you, we refund the unused part of what you prepaid.

## 13. Disclaimers

Glonet is provided as is. We do not verify members, their claims, their businesses or their credentials, and we are not part of anything you agree with another member. Use your own judgment before meeting, contracting with, hiring, investing in or paying anyone you meet here.

## 14. Limitation of liability

To the extent the law allows: we are not liable for indirect, incidental, special or consequential damages, or for lost profits, lost opportunities or lost data. Our total liability for any claim relating to Glonet is limited to the greater of the amount you paid us in the 12 months before the claim, or $50.

## 15. Indemnification

You will defend and hold harmless Mind Machine Learning from claims, damages and costs arising out of your use of Glonet, your content, or your dealings with other members.

## 16. Disputes

Disputes will be resolved by binding arbitration in San Jose, California, on an individual basis. You and we both waive the right to a jury trial and the right to take part in a class action. These Terms are governed by the laws of the State of California, without regard to its conflict of laws rules. Either of us may still seek an injunction in court to protect intellectual property or stop unauthorized access.

## 17. Changes to these Terms

We may update these Terms. The version and date at the top change when we do. If a change is material, we will let you know and ask you to accept the new version before you keep using Glonet. Continuing to use the service after a non-material change means you accept it.

## 18. Contact

Questions about these Terms: bhartsb@gmail.com`;

export const privacyPolicy = `# Privacy Policy

Last updated: July 27, 2026 (version 2026-07-27)

This policy explains what Glonet collects, why, who else touches it, and what you can do about it. Glonet is operated by Mind Machine Learning.

## 1. What we collect

### 1.1 You give us
- Account details: name, email address, and a phone number if you choose to add one. If you sign in with Google, we receive your name, email address and profile picture from Google. We never receive your Google password.
- Your profile: your objective, your offering, and any contact or social details you add, such as LinkedIn, a website, or a calendar link.
- What you say to our voice agents, as audio and as a transcript.
- Payment details, which go directly to Stripe. We see enough to know what you bought and whether it was paid. We never see or store your card number.
- Anything you send us: host applications, affiliate applications, feedback, support messages.

### 1.2 We generate or observe
- Matching results and the scores behind them.
- Water Cooler activity: when you entered and left, how long you expected to stay, where you went, games you played, and your sessions with the concierge agent.
- A readiness score for your profile, produced by AI, used to decide whether your profile is complete enough to match well.
- Ordinary technical and usage data: log records, device and browser information, and errors.

## 2. Why we use it

- To run the service: build your profile, match you, let you into rooms, send the introductions you asked for.
- To process payments, pay hosts and affiliates, and keep the financial records the law requires.
- To email you about your account, your matches, your membership and, where you are connected to someone, when they arrive in the Water Cooler.
- To improve matching and the product.
- To keep Glonet safe: detect fraud and abuse, enforce our Terms, and comply with the law.

We do not sell your personal information. We do not run advertising on Glonet.

## 3. AI processing and the companies we rely on

Running Glonet means your data passes through service providers. They process it for us, under contract, for the purposes above:

- Amazon Web Services: hosting, database, email delivery, sign-in.
- Stripe: payments, subscriptions and payouts.
- ElevenLabs: the voice agents. Your spoken conversations with them are processed and retained on their systems as well as ours.
- OpenAI and Groq: language models that read a transcript to draft your profile, score its readiness, and generate other text and images in the product.
- LiveKit: live audio and video in the Water Cooler. Live audio and video is not recorded by Glonet.
- HeyGen: the video avatar the concierge uses.

## 4. What other members see

Members you are matched or connected with can see your name, your objective and offering, any contact details you added, and the concierge line if you approved one. You choose what goes in your profile, so you choose what is shared. Your phone number, your payment details and your activity records are never shown to other members.

## 5. Email you receive

You get service email: welcome and account messages, match introductions, membership and billing notices, and Water Cooler arrival notices for people you are connected to. You can unsubscribe from promotional email. Service email about your account or your payments cannot be turned off while you have an account.

## 6. How long we keep things

We keep your account and profile while your account exists. If your membership lapses we keep your profile so that returning restores it. When you delete your account we delete your personal information within a reasonable period, except records we must keep for tax, accounting, fraud-prevention or legal reasons, and except anything already shared with another member, which we cannot retrieve. Data held by our providers is deleted according to their retention schedules.

## 7. Your rights

You can:
- See the personal information we hold about you, and get a copy.
- Correct anything that is wrong — most of it you can edit yourself.
- Delete your account and your personal information.
- Ask us to stop sending promotional email.
- Object to or restrict some processing, and ask us to hand over your data in a portable form, where the law gives you that right.

Write to the address at the bottom and we will act on it. We will not treat you worse for exercising a right.

## 8. Security

We use reasonable technical and organizational measures to protect your information: encrypted connections, managed cloud infrastructure, access controls and secret storage. No service can promise perfect security, and we do not.

## 9. Where your data is

Glonet runs on infrastructure in the United States. If you use it from elsewhere, your information is transferred to and processed in the United States.

## 10. Children

Glonet is for adults. It is not for anyone under 18, and we do not knowingly collect information from anyone under 18. If we learn that we have, we delete it.

## 11. Changes to this policy

We may update this policy. The version and date at the top change when we do, and material changes are notified in the product.

## 12. Contact

Questions, requests, or anything about your data: bhartsb@gmail.com`;
